import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { PRODUCT_STOCK_NOTIFICATION } from '@/graphql/mutations';
import validateEmail from 'commons/validateEmail';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';

const PDPNotifyOnStock = ({ id }) => {
  const [userEmail, setUserEmail] = useState('');
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [formStatus, setFormStatus] = useState('');

  const [
    sendProductStockNotification,
    { data: productNotificationData },
  ] = useMutation(PRODUCT_STOCK_NOTIFICATION);

  const updateUserEmail = event => {
    if (!event.target) {
      return;
    }
    setUserEmail(event.target.value);
  };

  const sendUserEmailForNotification = () => {
    if (!userEmail) {
      setIsEmailValid(false);
      setFormStatus('Please enter a valid email address and try again');
      return;
    }
    setNotificationLoading(true);
    sendProductStockNotification({
      variables: {
        email: userEmail,
        product_id: id,
      },
    });
  };

  useEffect(() => {
    if (!userEmail?.length) {
      if (isEmailValid) {
        setIsEmailValid(false);
      }
      return;
    }
    if (formStatus) {
      setFormStatus('');
    }
    if (validateEmail(userEmail) && !isEmailValid) {
      setIsEmailValid(true);
    }
  }, [formStatus, isEmailValid, userEmail]);

  useEffect(() => {
    if (!productNotificationData) {
      return;
    }
    setNotificationLoading(false);
    if (productNotificationData.notifyMeAsProductInStock.success) {
      setFormStatus(
        'Thanks! We’ll notify you when this product becomes available.'
      );
      setUserEmail('');
    }
  }, [productNotificationData]);

  return (
    <div className="flex flex-col mt-6">
      <div className="text-gray mb-[.4rem] text-sm font-bold">
        Sign up to be notified when it comes back in stock
      </div>
      <div>
        <LabeledTextInput
          action={
            <button
              type="button"
              disabled={!isEmailValid}
              data-xc="notify-me-button"
              className={classnames(
                'text-sm bg-transparent text-brand text-center flex justify-center items-center px-4 py-[15px] border-0 font-bold cursor-pointer',
                {
                  'cursor-not-allowed': !isEmailValid,
                }
              )}
              onClick={sendUserEmailForNotification}
            >
              {notificationLoading ? <ProgressLoader /> : 'Notify Me'}
            </button>
          }
          actionHasPadding={false}
          data-xc="pdp-notify-on-stock-email-field"
          label="Email address"
          name="email"
          onChange={updateUserEmail}
          type="email"
          value={userEmail}
        />
      </div>
      <div
        className={classnames(
          'text-sm font-bold p-4 text-center opacity-0 transform-[opacity_.5s_ease-in-out]',
          {
            'opacity-100': !!formStatus.length,
          }
        )}
      >
        {formStatus}
      </div>
    </div>
  );
};

PDPNotifyOnStock.propTypes = {
  id: PropTypes.number,
};

export default PDPNotifyOnStock;
